import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SeoHead from '../components/SeoHead'; // Assurez-vous d'avoir ce composant ou retirez l'import si non utilisé
import { 
  FaMapMarkerAlt, FaStar, FaArrowRight, FaCheck, FaFire, FaHotel, FaUtensils, FaTree, FaStore 
} from 'react-icons/fa';

const SITE_NAME = "Forfeo Explore Québec";

// --- TYPES & CONFIGURATION SEO (VOTRE CODE) ---
type CitySeoConfig = {
  title: string;
  description: string;
  h1: string;
  intro: string;
  image?: string; // Ajout pour l'image de fond dynamique
  sections: { title: string; text: string }[];
  internalLinks: { label: string; to: string }[];
};

const normalize = (s: string) =>
  (s || "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[’']/g, "-")
    .replace(/[^a-z0-9\-]/g, "");

const CONFIG: Record<string, CitySeoConfig> = {
  "quebec/quoi-faire": {
    title: "Quoi faire à Québec | Activités, hôtels et expériences",
    description: "Guide pratique pour visiter Québec : idées d’activités, quartiers à explorer, hébergements, restos et offres Last Call de dernière minute.",
    h1: "Quoi faire à Québec",
    image: "https://images.unsplash.com/photo-1519885277449-12eeee55ef21?q=80&w=2000",
    intro: "Envie de planifier un séjour à Québec sans perdre 3 heures sur 25 onglets? Ici, tu trouves une sélection d’idées, de secteurs et de bons plans — et surtout un accès direct aux offres locales (dont les Last Call).",
    sections: [
      { title: "Les incontournables (sans tomber dans le piège touristique)", text: "Vieux-Québec, Terrasse Dufferin, Petit-Champlain, et une marche au bord du fleuve. Conseil: vise tôt le matin ou fin de journée pour une vibe plus “cinéma” et moins “queue de monde”." },
      { title: "Expériences locales qui convertissent en souvenirs", text: "Spa & thermes, microbrasseries, restos signature, activités de plein air, et sorties culturelles. Forfeo mise sur des partenaires locaux: tu peux tomber sur des expériences que Google ne te sert pas toujours en premier." },
      { title: "Last Call: le meilleur levier pour économiser", text: "Les commerces publient leurs invendus / disponibilités de dernière minute. Résultat: prix cassés, quantités limitées, et parfois des pépites premium qui partent vite." },
    ],
    internalLinks: [
      { label: "Voir les Offres Last Call", to: "/last-call" },
      { label: "Explorer les hôtels et commerces", to: "/explorer-plus?q=Québec" },
      { label: "Visiter Québec (mode immersion)", to: "/visiter-quebec" },
      { label: "Inscrire mon entreprise", to: "/register-business" },
    ],
  },
  "montreal/quoi-faire": {
    title: "Quoi faire à Montréal | Guide expériences, hôtels et bons plans",
    description: "Idées pour visiter Montréal: quartiers, activités, restos, hôtels, et offres Last Call. Un guide simple pour planifier vite et bien.",
    h1: "Quoi faire à Montréal",
    image: "https://images.unsplash.com/photo-1519178173668-2f63d5542a44?q=80&w=2000",
    intro: "Montréal, c’est la ville où tu peux bruncher fort, marcher beaucoup, manger encore, puis finir sur un rooftop. Voici une base solide pour organiser ton trip — avec des offres locales quand il reste des dispos.",
    sections: [
      { title: "Quartiers à explorer", text: "Vieux-Montréal, Plateau, Mile End, Griffintown, et le canal Lachine. Le move: choisis 2 quartiers par jour max pour profiter au lieu de courir." },
      { title: "Activités qui valent la peine", text: "Musées, festivals, food spots, expériences bien-être et activités urbaines. Le combo gagnant: 1 activité “iconique” + 1 expérience locale moins connue." },
      { title: "Last Call à Montréal", text: "Les offres de dernière minute peuvent être incroyables (surtout resto & activités). Si tu es flexible sur l’heure: jackpot." },
    ],
    internalLinks: [
      { label: "Offres Last Call", to: "/last-call" },
      { label: "Explorer Montréal", to: "/explorer-plus?q=Montréal" },
      { label: "Inscrire mon entreprise", to: "/register-business" },
    ],
  },
  "charlevoix/quoi-faire": {
    title: "Quoi faire à Charlevoix | Nature, gastronomie et hébergements",
    description: "Guide Charlevoix: activités nature, routes panoramiques, hébergements, restos et offres Last Call. Idéal pour un week-end ou roadtrip.",
    h1: "Quoi faire à Charlevoix",
    image: "https://images.unsplash.com/photo-1599423300746-b62507ac9705?q=80&w=2000",
    intro: "Charlevoix = panoramas + bouffe + air frais qui remet ton cerveau en “mode calme”. Parfait pour un week-end ou une micro-aventure.",
    sections: [
      { title: "Roadtrip panoramique", text: "Prends la route pour les points de vue, arrêts photo, et villages. Fais simple: 1-2 gros stops par jour + restos locaux." },
      { title: "Expériences signature", text: "Randos, spa, dégustations, activités saisonnières. Les partenaires locaux, c’est souvent là que la magie arrive (petits spots, grosse qualité)." },
      { title: "Bons plans Last Call", text: "Quand il reste des dispos, certains hébergements et activités sortent des rabais rapides. Idéal si tu peux partir sur un coup de tête." },
    ],
    internalLinks: [
      { label: "Voir Last Call", to: "/last-call" },
      { label: "Explorer Charlevoix", to: "/explorer-plus?q=Charlevoix" },
      { label: "Inscrire mon entreprise", to: "/register-business" },
    ],
  },
  "gaspesie/roadtrip": {
    title: "Roadtrip Gaspésie | Itinéraire, arrêts et bons plans",
    description: "Roadtrip en Gaspésie: itinéraire, arrêts, activités et astuces. Trouvez aussi des offres Last Call de dernière minute.",
    h1: "Roadtrip en Gaspésie",
    image: "https://images.unsplash.com/photo-1610988630048-c89b33534b8c?q=80&w=2000",
    intro: "La Gaspésie, c’est le genre de roadtrip où tu reviens avec 800 photos et un besoin étrange de retourner voir le Rocher Percé “juste une dernière fois”.",
    sections: [
      { title: "Itinéraire simple (sans t’épuiser)", text: "Planifie par zones (ex: 2 nuits par secteur). Ajoute des “buffers” pour les arrêts imprévus: c’est là que les meilleurs moments arrivent." },
      { title: "Expériences à ne pas manquer", text: "Nature, points de vue, activités maritimes, bouffe locale. Check les activités selon la saison: ça change tout." },
      { title: "Last Call (oui, même en roadtrip)", text: "Si tu es flexible, tu peux tomber sur des rabais de dernière minute sur des activités ou des spots disponibles le jour-même." },
    ],
    internalLinks: [
      { label: "Voir Last Call", to: "/last-call" },
      { label: "Explorer Gaspésie", to: "/explorer-plus?q=Gaspésie" },
      { label: "Inscrire mon entreprise", to: "/register-business" },
    ],
  },
  "last-call": {
    title: "Offres Last Call Québec | Rabais dernière minute (-50% et +)",
    description: "Découvrez les offres Last Call: invendus de dernière minute à prix cassé. Quantités limitées, premiers arrivés, premiers servis.",
    h1: "Offres Last Call au Québec",
    image: "https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?q=80&w=2000",
    intro: "C’est la section “deal intelligent” sans dénaturer Forfeo: des invendus / disponibilités de dernière minute, publiés par des entreprises locales.",
    sections: [
      { title: "Pourquoi ça marche", text: "Les commerces remplissent leurs trous. Les voyageurs économisent. Et toi, tu découvres des places que tu n’aurais jamais testées plein prix." },
      { title: "Comment en profiter", text: "Sois flexible sur l’heure et clique vite. Les meilleures offres partent vite, surtout quand la quantité est limitée." },
    ],
    internalLinks: [
      { label: "Aller aux offres Last Call", to: "/last-call" },
      { label: "Explorer tout le réseau", to: "/explorer-plus" },
      { label: "Inscrire mon entreprise", to: "/register-business" },
    ],
  },
};

export default function SeoCityLanding() {
  const { city, slug } = useParams();
  const navigate = useNavigate();
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // --- LOGIQUE COMBINÉE ---
  const normalizedCity = normalize(city || "");
  const normalizedSlug = normalize(slug || "");
  
  // Clé pour la config SEO (ex: "quebec/quoi-faire")
  // Si le slug est vide (ex: /quebec/last-call), on gère le cas spécial
  const key = slug ? `${normalizedCity}/${normalizedSlug}` : normalizedCity; 
  
  // On cherche la config exacte, sinon on prend une config par défaut générique
  const cfg = CONFIG[key] || CONFIG["last-call"] || {
    title: `${SITE_NAME} | Guide`,
    description: "Guides et bons plans au Québec: activités, hôtels, expériences et offres Last Call.",
    h1: city ? `Découvrir ${city.charAt(0).toUpperCase() + city.slice(1)}` : "Guide Québec",
    intro: "Page guide. Explorez les expériences, hôtels et offres disponibles.",
    image: "https://images.unsplash.com/photo-1490220275828-9c595086d4de?q=80&w=2000",
    sections: [],
    internalLinks: [
      { label: "Explorer", to: "/explorer-plus" },
      { label: "Last Call", to: "/last-call" },
      { label: "Inscrire mon entreprise", to: "/register-business" },
    ],
  };

  // --- CHARGEMENT DYNAMIQUE DES DONNÉES SUPABASE (MON AJOUT) ---
  useEffect(() => {
    fetchRelevantItems();
  }, [city, slug]);

  const fetchRelevantItems = async () => {
    setLoading(true);
    let query = supabase.from('businesses').select('*').eq('is_approved', true);

    // Filtrage par ville (si pertinent)
    if (city && !key.includes('last-call')) {
        query = query.ilike('city', `%${city}%`);
    }

    // Filtrage thématique simple basé sur le slug ou la config
    if (normalizedSlug.includes('hotel') || normalizedSlug.includes('hebergement')) {
        query = query.eq('category', 'Hébergement');
    } else if (normalizedSlug.includes('resto') || normalizedSlug.includes('manger')) {
        query = query.eq('category', 'Restauration');
    }

    const { data } = await query.limit(12);
    
    // Si pas assez de résultats, on complète avec des suggestions globales
    if (!data || data.length < 3) {
        const { data: globalData } = await supabase.from('businesses').select('*').eq('is_approved', true).limit(6);
        setItems(globalData || []);
    } else {
        setItems(data);
    }
    setLoading(false);
  };

  const canonicalPath = `/quebec/${normalizedCity}/${normalizedSlug}`;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col font-sans">
      <Header />

      {/* Composant SEO Head (si disponible, sinon on peut l'ignorer ou le recréer) */}
      {/* <SeoHead title={cfg.title} description={cfg.description} canonicalPath={canonicalPath} /> */}

      {/* --- HERO HEADER --- */}
      <div className="relative h-[60vh] flex items-center justify-center text-center text-white overflow-hidden">
        <div className="absolute inset-0 bg-black/50 z-10"></div>
        <img 
            src={cfg.image} 
            alt={cfg.title} 
            className="absolute inset-0 w-full h-full object-cover animate-slow-zoom"
        />
        
        <div className="relative z-20 container mx-auto px-4">
            <span className="inline-flex items-center gap-2 bg-blue-600/80 border border-blue-400 backdrop-blur-md px-4 py-1.5 rounded-full text-[10px] md:text-xs font-black uppercase tracking-widest mb-6 animate-in fade-in slide-in-from-bottom-4">
                <FaMapMarkerAlt /> Guide Officiel Forfeo
            </span>
            <h1 className="text-4xl md:text-6xl font-black mb-6 leading-tight animate-in fade-in slide-in-from-bottom-6 duration-700">
                {cfg.h1}
            </h1>
            <p className="text-lg md:text-xl text-slate-200 max-w-2xl mx-auto font-medium animate-in fade-in slide-in-from-bottom-8 duration-700 delay-100 leading-relaxed">
                {cfg.intro}
            </p>

            {/* CTA BAR */}
            <div className="mt-8 flex flex-wrap justify-center gap-3 animate-in fade-in slide-in-from-bottom-10 delay-200">
                <Link to="/last-call" className="flex items-center justify-center gap-2 bg-slate-900 text-white px-6 py-3 rounded-2xl font-black uppercase tracking-widest text-xs hover:bg-slate-800 transition shadow-lg">
                    <FaFire className="text-yellow-300" /> Offres Last Call <FaArrowRight />
                </Link>
                <Link to="/explorer-plus" className="flex items-center justify-center gap-2 bg-white text-slate-900 px-6 py-3 rounded-2xl font-black uppercase tracking-widest text-xs hover:bg-slate-100 transition shadow-lg">
                    Explorer le réseau <FaArrowRight />
                </Link>
            </div>
        </div>
      </div>

      {/* --- FILTRES RAPIDES --- */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-40 shadow-sm">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between overflow-x-auto">
             <div className="flex gap-2 text-xs font-bold text-slate-500 whitespace-nowrap">
                <Link to="/" className="hover:text-blue-600">Accueil</Link> / 
                <span className="text-slate-900">{city || "Guide"}</span>
             </div>

             <div className="flex gap-2">
                <button onClick={() => navigate(`/quebec/${city || 'quebec'}/hotels`)} className="px-3 py-1.5 rounded-full bg-slate-100 hover:bg-blue-50 text-slate-700 hover:text-blue-600 text-[10px] font-black uppercase tracking-widest flex items-center gap-2 transition">
                    <FaHotel/> Dormir
                </button>
                <button onClick={() => navigate(`/quebec/${city || 'quebec'}/restaurants`)} className="px-3 py-1.5 rounded-full bg-slate-100 hover:bg-blue-50 text-slate-700 hover:text-blue-600 text-[10px] font-black uppercase tracking-widest flex items-center gap-2 transition">
                    <FaUtensils/> Manger
                </button>
                <button onClick={() => navigate(`/quebec/${city || 'quebec'}/activites`)} className="px-3 py-1.5 rounded-full bg-slate-100 hover:bg-blue-50 text-slate-700 hover:text-blue-600 text-[10px] font-black uppercase tracking-widest flex items-center gap-2 transition">
                    <FaTree/> Bouger
                </button>
             </div>
        </div>
      </div>

      <main className="flex-1 container mx-auto px-4 py-16">
        
        {/* --- SECTIONS TEXTUELLES (VOTRE CODE) --- */}
        <div className="grid md:grid-cols-3 gap-8 mb-20">
            {cfg.sections.map((s, i) => (
                <section key={i} className="bg-white border border-slate-100 rounded-[2rem] p-8 shadow-sm hover:shadow-md transition-shadow">
                    <h2 className="text-xl font-black text-slate-900 mb-4 leading-tight">
                        {s.title}
                    </h2>
                    <p className="text-slate-600 text-sm leading-relaxed">{s.text}</p>
                </section>
            ))}
        </div>

        {/* --- LISTE DYNAMIQUE (MON AJOUT) --- */}
        <div className="mb-20">
            <div className="flex items-center gap-3 mb-10">
                <div className="w-10 h-1 bg-blue-600 rounded-full"></div>
                <h2 className="text-2xl font-black text-slate-900 uppercase tracking-tight">Notre sélection locale</h2>
            </div>

            {loading ? (
                <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {items.map((biz) => (
                        <div key={biz.id} onClick={() => navigate(`/place/${biz.id}`)} className="group bg-white rounded-[2rem] overflow-hidden border border-slate-100 shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 cursor-pointer">
                            <div className="relative h-64 overflow-hidden">
                                <img 
                                    src={biz.image_url || "https://placehold.co/600x400?text=Forfeo"} 
                                    className="w-full h-full object-cover group-hover:scale-110 transition duration-700"
                                    alt={biz.name}
                                />
                                <div className="absolute top-4 left-4 bg-white/90 backdrop-blur px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest text-slate-900 shadow-sm">
                                    {biz.category}
                                </div>
                                {biz.is_pro && (
                                    <div className="absolute top-4 right-4 bg-yellow-400 text-slate-900 p-2 rounded-full shadow-md" title="Partenaire Vérifié">
                                        <FaCheck size={12}/>
                                    </div>
                                )}
                            </div>
                            
                            <div className="p-6">
                                <div className="flex justify-between items-start mb-2">
                                    <h3 className="text-xl font-black text-slate-900 leading-tight group-hover:text-blue-600 transition">{biz.name}</h3>
                                    <div className="flex items-center gap-1 text-yellow-500 text-xs font-bold bg-yellow-50 px-2 py-1 rounded-lg">
                                        <FaStar/> 4.8
                                    </div>
                                </div>
                                
                                <div className="flex items-center gap-2 text-slate-400 text-xs font-bold mb-4 uppercase tracking-wide">
                                    <FaMapMarkerAlt/> {biz.city}
                                </div>

                                <p className="text-slate-600 text-sm line-clamp-2 mb-6">
                                    {biz.description || "Découvrez ce lieu unique au cœur du Québec. Une expérience inoubliable vous attend avec Forfeo."}
                                </p>

                                <div className="pt-4 border-t border-slate-100 flex items-center justify-between">
                                    <span className="text-xs font-black text-slate-400 uppercase tracking-widest group-hover:text-blue-600 transition flex items-center gap-2">
                                        Voir la fiche <FaArrowRight/>
                                    </span>
                                    <span className="flex items-center gap-1 text-[10px] font-bold text-red-500 bg-red-50 px-2 py-1 rounded-full">
                                        <FaFire/> Populaire
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>

        {/* --- FAQ SEO & LIENS INTERNES (VOTRE CODE) --- */}
        <div className="grid md:grid-cols-2 gap-12 pt-10 border-t border-slate-200">
             {/* Liens internes */}
             <div>
                <h3 className="text-sm font-black uppercase tracking-widest text-slate-400 mb-6">
                  Aller plus loin
                </h3>
                <div className="flex flex-col gap-3">
                  {cfg.internalLinks.map((l, idx) => (
                    <Link
                      key={idx}
                      to={l.to}
                      className="group flex items-center justify-between bg-white border border-slate-200 hover:border-blue-300 hover:bg-blue-50 transition p-4 rounded-2xl text-sm font-bold text-slate-700"
                    >
                      {l.label} <FaArrowRight className="text-slate-300 group-hover:text-blue-500 transition" />
                    </Link>
                  ))}
                </div>
            </div>

            {/* Mini FAQ Générique */}
            <div className="bg-slate-50 rounded-[2rem] p-8 border border-slate-100">
                <h2 className="text-2xl font-black text-slate-900 mb-6">Questions fréquentes</h2>
                <div className="space-y-6 text-slate-700 text-sm">
                    <div>
                        <p className="font-black mb-1">Forfeo vend des vols?</p>
                        <p className="text-slate-500">
                        Forfeo Explore compare et vous aide à planifier. Le focus SEO/produit principal ici: hôtels, expériences locales et Last Call.
                        </p>
                    </div>
                    <div>
                        <p className="font-black mb-1">C’est quoi Last Call?</p>
                        <p className="text-slate-500">
                        Des offres de dernière minute publiées par les entreprises locales (invendus / disponibilités). Quantités limitées.
                        </p>
                    </div>
                    <div>
                        <p className="font-black mb-1">Comment ça aide les entreprises?</p>
                        <p className="text-slate-500">
                        Visibilité + conversion + remplissage des périodes creuses, avec un tableau de bord pour gérer tout.
                        </p>
                    </div>
                </div>
            </div>
        </div>

      </main>

      <Footer />
    </div>
  );
}
